import { Button, Modal, Select } from "antd";
import { ArrowRight, ChevronLeft, Save } from "lucide-react";
import { FC, useCallback, useMemo } from "react";
import { useNotification } from "~/hooks";
import { useUpdateInventory } from "~/services/inventory";
import { useGetClients } from "~/services/client/client.queries";
import { ClientStatus } from "~/utils/constants";
import { Steps } from "../steps";
import { ReviewConfirm } from "./review-confirm";
import { SelectContainer } from "./select-container";
import { SelectPieces } from "./select-pieces";
import { useDeconsolidateStore } from "./deconsolidate.store";

type Props = {
  open: boolean;
  onCancel: () => void;
};

export const DeconsolidateInventory: FC<Props> = ({ open, onCancel }) => {
  const { success, error } = useNotification();
  const {
    step,
    setStep,
    clientId,
    setClientId,
    selectedPieces,
    selectedContainer,
    resetState,
  } = useDeconsolidateStore();

  const { data: clients, isLoading: clientsLoading } = useGetClients({
    params: { status: ClientStatus.ACTIVE, sortByName: "ASC", getAll: true },
  });

  const { mutate, isPending } = useUpdateInventory();

  // Handle final deconsolidation
  const handleDeconsolidate = () => {
    if (!selectedContainer || selectedPieces.length === 0) return;

    mutate(
      {
        id: selectedContainer?.id,
        pieceIdsToRemove: selectedPieces.map((piece) => piece.id),
      },
      {
        onSuccess: () => {
          success({
            message: "Deconsolidation Successful",
            description: "Inventory deconsolidated successfully.",
          });
          onCancel();
        },
        onError: (err) => {
          // Handle error
          console.error(err);
          error({
            message: "Deconsolidation Failed",
            description: "Failed to deconsolidate inventory.",
          });
        },
      }
    );
  };

  const goToNextStep = useCallback(() => {
    setStep(step + 1);
  }, [step, setStep]);

  const goToPreviousStep = () => {
    setStep(step - 1);
  };

  const closeDeconsolidate = () => {
    resetState();
    onCancel();
  };

  const steps = useMemo(
    () => [
      {
        key: 1,
        label: "Select Container",
        content: <SelectContainer goToNextStep={goToNextStep} />,
      },
      {
        key: 2,
        label: "Select Pieces",
        content: <SelectPieces />,
      },
      {
        key: 3,
        label: "Review & Confirm",
        content: <ReviewConfirm />,
      },
    ],
    [goToNextStep]
  );

  return (
    <Modal open={open} onCancel={closeDeconsolidate} width={1000} footer={null}>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center space-x-2 mb-1">
            <h1 className="text-2xl font-semibold">Deconsolidate Inventory</h1>
          </div>
          <p className="text-gray-600">
            Remove pieces from a container to manage them individually.
          </p>
        </div>

        {/* Client Selector */}
        <div className="mb-6 p-4 bg-gray-50 border rounded-lg">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Client
          </label>
          <Select
            showSearch
            placeholder="Select a client to filter containers"
            optionFilterProp="children"
            loading={clientsLoading}
            filterOption={(input, option) =>
              (option?.label ?? "")
                .toLowerCase()
                .includes(input.toLowerCase())
            }
            value={clientId}
            onChange={(value) => setClientId(value)}
            options={clients?.items?.map((client) => ({
              value: client.id,
              label:
                client.companyName ||
                [client.firstName, client.lastName]
                  .filter(Boolean)
                  .join(" ")
                  .trim() ||
                "Unnamed Client",
            }))}
            className="w-full"
            size="large"
          />
          {!clientId && (
            <p className="text-sm text-amber-600 mt-2">
              Please select a client to view their containers.
            </p>
          )}
        </div>

        {clientId ? (
          <>
            {/* Progress Steps */}
            <Steps current={step} steps={steps} />

            {steps[step - 1].content}

            <div className="pt-5 flex justify-between">
              {step > 1 ? (
                <Button
                  size="large"
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50 flex items-center space-x-2"
                  onClick={goToPreviousStep}
                >
                  <ChevronLeft size={16} />
                  <span>Back</span>
                </Button>
              ) : (
                <div />
              )}
              {step < steps.length ? (
                <Button
                  size="large"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={goToNextStep}
                  disabled={
                    (step === 1 && !selectedContainer) ||
                    (step === 2 && selectedPieces.length === 0)
                  }
                >
                  <span>Continue</span>
                  <ArrowRight size={16} />
                </Button>
              ) : null}
              {step === steps.length && (
                <Button
                  size="large"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
                  onClick={handleDeconsolidate}
                  disabled={!selectedContainer || selectedPieces.length === 0}
                  loading={isPending}
                >
                  <Save size={16} />
                  <span>Confirm Deconsolidation</span>
                </Button>
              )}
            </div>
          </>
        ) : (
          <div className="py-12 text-center text-gray-400">
            <p>Select a client above to begin deconsolidating inventory.</p>
          </div>
        )}
      </div>
    </Modal>
  );
};
